import React, { useState, useEffect } from "react";
import { useStyles } from "../../assets/jss/AnalyticsExports";
import CustomGrid from "../CustomComponents/CustomGrid/CustomGrid";
import CustomCheckbox from "../CustomComponents/CustomCheckbox/CustomCheckbox";
import CustomPaper from "../CustomComponents/CustomPaper/CustomPaper";
import CustomCard from "../CustomComponents/CustomCard/CustomCard";
import CustomCardContent from "../CustomComponents/CustomCardContent/CustomCardContent";
import CustomLineChart from "../CustomComponents/CustomLineChart/CustomLineChart";
import CustomCircularProgress from "../CustomComponents/CustomCircularProgress/CustomCircularProgress";
import CustomTypography from "../CustomComponents/CustomTypography/CustomTypography";
import clsx from "clsx";
import trackEvents from "../../assets/appInsightsEvents";
import { EntityAccess, EventCode, RBAC_CODE, Role } from "../../config/app-globals.enum";
import moment from "moment";
import { getSession, getUserRBAC, isShowEventScore } from "../../services/LocalStorageService";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

export default function FleetPerformance(props: any) {
    const params = useParams();
    const { t } = useTranslation();
    const classes = useStyles();
    const [getRBAC_PerformanceCard] = React.useState(
        getUserRBAC(RBAC_CODE.PERFORMANCE_CARD_VIEW, props)
    );
    const [userData] = useState(props.userData);
    const [fleetPerformance, setFleetPerformance] = useState(null);
    const [fleetPerformanceComputed, setFleetPerformanceComputed] = useState(null);
    const [userSession] = useState(getSession);
    const [isApiFailed, setIsApiFailed] = useState(null);
    const [performanceScoreData, setPerformanceScoreData] = useState(null);
    const [isApi, setIsApi] = useState(null);
    const [isShowSpeeding] = React.useState(isShowEventScore(EventCode.SPEEDING));
    const [isShowBraking] = React.useState(isShowEventScore(EventCode.BRAKING));
    const [isShowCornering] = React.useState(isShowEventScore(EventCode.CORNERING));
    const [isShowDistraction] = React.useState(isShowEventScore(EventCode.DRIVER_DISTRACTION));
    const [isShowAcceleration] = React.useState(isShowEventScore(EventCode.ACCELERATION));

    useEffect(() => {
        if (props.eventScore) {
            if (!isShowBraking) {
                if (props.eventScore && props.eventScore.brakingScoreData) {
                    delete props.eventScore["brakingScoreData"];
                }
            }
            if (!isShowCornering) {
                if (props.eventScore && props.eventScore.corneringScoreData) {
                    delete props.eventScore["corneringScoreData"];
                }
            }
            if (!isShowSpeeding) {
                if (props.eventScore && props.eventScore.speedingScoreData) {
                    delete props.eventScore["speedingScoreData"];
                }
            }
            if (!isShowDistraction) {
                if (props.eventScore && props.eventScore.distractionScoreData) {
                    delete props.eventScore["distractionScoreData"];
                }
            }
            if (!isShowAcceleration) {
                if (props.eventScore && props.eventScore.accelerationScoreData) {
                    delete props.eventScore["accelerationScoreData"];
                }
            }
            setPerformanceScoreData(props.eventScore);
        }
    }, [props.eventScore]);

    useEffect(() => {
        setIsApiFailed(props.isApiFailed);
    }, [props.isApiFailed]);

    useEffect(() => {
        if (props.eventScoreComputed) {
            setFleetPerformanceComputed(props.eventScoreComputed);
        }
    }, [props.eventScoreComputed]);

    useEffect(() => {
        setIsApi(props.isApi);
    }, [props.isApi]);

    useEffect(() => {
        if (performanceScoreData && fleetPerformanceComputed) {
            let labels = [];
            let datasets = [];
            if (performanceScoreData.brakingScoreData) {
                const brakingLabels = performanceScoreData.brakingScoreData.map((item) =>
                    moment(item.scoreDate).format("DD MMM")
                );
                labels.push(...brakingLabels);
                const brakingBarData = performanceScoreData.brakingScoreData.map((item) =>
                    (item.score === null || item.score === undefined) ? null : item.score
                );
                let dataset = {
                    label: t("BRAKING"),
                    backgroundColor: "rgba(250,100,0,1)",
                    borderColor: "rgba(250,100,0,.7)",
                    lineTension: 0.1,
                    borderWidth: 2,
                    fill: false,
                    data: brakingBarData,
                }
                datasets.push(dataset);
            }


            if (performanceScoreData.corneringScoreData) {
                const corneringLabels = performanceScoreData.corneringScoreData.map((item) =>
                    moment(item.scoreDate).format("DD MMM")
                );
                labels.push(...corneringLabels);
                const corneringBarData = performanceScoreData.corneringScoreData.map((item) =>
                    (item.score === null || item.score === undefined) ? null : item.score
                );
                let dataset = {
                    label: t("CORNERING"),
                    backgroundColor: "rgba(0,98,250,1)",
                    borderColor: "rgba(0,98,250,.7)",
                    lineTension: 0.1,
                    borderWidth: 2,
                    fill: false,
                    data: corneringBarData,
                }
                datasets.push(dataset);
            }

            if (performanceScoreData.speedingScoreData) {
                const speedingLabels = performanceScoreData.speedingScoreData.map((item) =>
                    moment(item.scoreDate).format("DD MMM")
                );
                labels.push(...speedingLabels);
                const speedingBarData = performanceScoreData.speedingScoreData.map((item) =>
                    (item.score === null || item.score === undefined) ? null : item.score
                );
                let dataset = {
                    label: t("SPEEDING"),
                    backgroundColor: "rgba(200,0,250,1)",
                    borderColor: "rgba(200,0,250,1)",
                    lineTension: 0.1,
                    borderWidth: 2,
                    fill: false,
                    data: speedingBarData,
                }
                datasets.push(dataset);
            }

            if (performanceScoreData.accelerationScoreData) {

                const accelerationLabels = performanceScoreData.accelerationScoreData.map((item) =>
                    moment(item.scoreDate).format("DD MMM")
                );

                labels.push(...accelerationLabels);
                const accelerationBarData = performanceScoreData.accelerationScoreData.map((item) =>
                    (item.score === null || item.score === undefined) ? null : item.score
                );
                let dataset = {
                    label: t("ACCELERATION"),
                    backgroundColor: "#40E0D0",
                    borderColor: "#40E0D0",
                    lineTension: 0.1,
                    borderWidth: 2,
                    fill: false,
                    data: accelerationBarData,
                }
                datasets.push(dataset);
            }

            if (performanceScoreData.engagementScoreData) {
                const engagementLabels = performanceScoreData.engagementScoreData.map((item) =>
                    moment(item.scoreDate).format("DD MMM")
                );
                labels.push(...engagementLabels);
                const engagementBarData = performanceScoreData.engagementScoreData.map((item) =>
                    (item.score === null || item.score === undefined) ? null : item.score
                );
                let dataset = {
                    label: t("ENGAGEMENT"),
                    backgroundColor: "rgba(105,164,22,1)",
                    borderColor: "rgba(105,164,22,1)",
                    lineTension: 0.1,
                    borderWidth: 2,
                    fill: false,
                    data: engagementBarData,
                }
                datasets.push(dataset);
            }

            if (performanceScoreData.distractionScoreData) {
                const distractionLabels = performanceScoreData.distractionScoreData.map((item) =>
                    moment(item.scoreDate).format("DD MMM")
                );
                labels.push(...distractionLabels);
                const distractionBarData = performanceScoreData.distractionScoreData.map((item) =>
                    (item.score === null || item.score === undefined) ? null : item.score
                );
                let dataset = {
                    label: t("DISTRACTION"),
                    backgroundColor: "#f9d037",
                    borderColor: "#f9d037",
                    lineTension: 0.1,
                    borderWidth: 2,
                    fill: false,
                    data: distractionBarData,
                }
                datasets.push(dataset);
            }

            // const corneringLabels = performanceScoreData.corneringScoreData.map(
            //     (item) => moment(item.scoreDate).format("DD MMM")
            // );
            // const speedingLabels = performanceScoreData.speedingScoreData.map(
            //     (item) => moment(item.scoreDate).format("DD MMM")
            // );
            // const engagementLabels = performanceScoreData.engagementScoreData.map(
            //     (item) => moment(item.scoreDate).format("DD MMM")
            // );
            // let labels = [
            //     ...brakingLabels,
            //     ...corneringLabels,
            //     ...speedingLabels,
            //     ...engagementLabels,
            // ];
            labels = [...new Set(labels)];
            /* Why was this sorted? */
            // labels.sort((item1, item2) => new Date(item1) - new Date(item2));
            // const brakingBarData = performanceScoreData.brakingScoreData.map((item) =>
            //     item.score ? item.score : 0
            // );
            // const corneringBarData = performanceScoreData.corneringScoreData.map(
            //     (item) => (item.score ? item.score : 0)
            // );
            // const speedingBarData = performanceScoreData.speedingScoreData.map(
            //     (item) => (item.score ? item.score : 0)
            // );

            // const engagementBarData = performanceScoreData.engagementScoreData.map(
            //     (item) => (item.score ? item.score : 0)
            // );
            const performanceGraph = {
                loading: false,
                driverPerformance: {
                    labels: labels,
                    datasets: datasets,
                },
                driverPerformanceCheckbox: {
                    braking: true,
                    brakingData: {},
                    cornering: true,
                    corneringData: {},
                    speeding: true,
                    speedingData: {},
                    engagement: true,
                    engagementData: {},
                    distraction: true,
                    distractionData: {},
                    acceleration: true,
                    accelerationData: {}
                },
                avgCorneringScore: fleetPerformanceComputed.cornering ?
                    Math.round(
                        (fleetPerformanceComputed.cornering.avgScore + Number.EPSILON) * 100
                    ) / 100 : "N/A",
                avgBrakingScore: fleetPerformanceComputed.braking ?
                    Math.round(
                        (fleetPerformanceComputed.braking.avgScore + Number.EPSILON) * 100
                    ) / 100 : "N/A",
                avgSpeedingScore: fleetPerformanceComputed.speeding ?
                    Math.round(
                        (fleetPerformanceComputed.speeding.avgScore + Number.EPSILON) * 100
                    ) / 100 : "N/A",
                avgEngagementScore: fleetPerformanceComputed.engagement ?
                    Math.round(
                        (fleetPerformanceComputed.engagement.avgScore + Number.EPSILON) *
                        100
                    ) / 100 : "N/A",
                avgDistractionScore: fleetPerformanceComputed.distraction ?
                    Math.round(
                        (fleetPerformanceComputed.distraction.avgScore + Number.EPSILON) *
                        100
                    ) / 100 : "N/A",
                avgAccelerationScore: fleetPerformanceComputed.acceleration ?
                    Math.round(
                        (fleetPerformanceComputed.acceleration.avgScore + Number.EPSILON) *
                        100
                    ) / 100 : "N/A",
            };

            setFleetPerformance(performanceGraph);
        }
    }, [performanceScoreData]);

    useEffect(() => {
        if (userSession && (isApiFailed || !performanceScoreData)) {
            const dateArray = [];
            const randomDataBraking = [];
            const randomDataCornering = [];
            const randomDataSpeeding = [];
            const randomDataEngagement = [];
            const randomDataDistraction = [];
            const randomDataAcceleration = [];
            let startdate = new Date(userSession.startDate);
            const enddate = new Date(userSession.endDate);
            while (startdate <= enddate) {
                dateArray.push(moment(startdate).format("DD MMM"));
                randomDataBraking.push(Math.random() * 100);
                randomDataCornering.push(Math.random() * 100);
                randomDataSpeeding.push(Math.random() * 100);
                randomDataEngagement.push(Math.random() * 100);
                randomDataDistraction.push(Math.random() * 100);
                randomDataAcceleration.push(Math.random() * 100);
                startdate.setDate(startdate.getDate() + 1);
            }
            const performanceGraph = {
                loading: false,
                driverPerformance: {
                    labels: dateArray,
                    datasets: [
                        {
                            label: t("BRAKING"),
                            backgroundColor: "rgb(241, 242, 246)",
                            borderColor: "rgb(241, 242, 246)",
                            lineTension: 0.1,
                            borderWidth: 2,
                            fill: false,
                            data: randomDataBraking,
                        },
                        {
                            label: t("CORNERING"),
                            backgroundColor: "rgb(241, 242, 246)",
                            borderColor: "rgb(241, 242, 246)",
                            lineTension: 0.1,
                            borderWidth: 2,
                            fill: false,
                            data: randomDataCornering,
                        },
                        {
                            label: t("SPEEDING"),
                            backgroundColor: "rgb(241, 242, 246)",
                            borderColor: "rgb(241, 242, 246)",
                            lineTension: 0.1,
                            borderWidth: 2,
                            fill: false,
                            data: randomDataSpeeding,
                        },
                        {
                            label: t("ENGAGEMENT"),
                            backgroundColor: "rgb(241, 242, 246)",
                            borderColor: "rgb(241, 242, 246)",
                            lineTension: 0.1,
                            borderWidth: 2,
                            fill: false,
                            data: randomDataEngagement,
                        },
                        {
                            label: t("DISTRACTION"),
                            backgroundColor: "rgb(241, 242, 246)",
                            borderColor: "rgb(241, 242, 246)",
                            lineTension: 0.1,
                            borderWidth: 2,
                            fill: false,
                            data: randomDataDistraction,
                        },
                        {
                            label: t("ACCELERATION"),
                            backgroundColor: "rgb(241, 242, 246)",
                            borderColor: "rgb(241, 242, 246)",
                            lineTension: 0.1,
                            borderWidth: 2,
                            fill: false,
                            data: randomDataAcceleration,
                        },
                    ],
                },
                driverPerformanceCheckbox: {
                    braking: true,
                    brakingData: {},
                    cornering: true,
                    corneringData: {},
                    speeding: true,
                    speedingData: {},
                    engagement: true,
                    engagementData: {},
                    distraction: true,
                    distractionData: {},
                    acceleration: true,
                    accelerationData: {},
                },
                avgCorneringScore: "N/A",
                avgBrakingScore: "N/A",
                avgSpeedingScore: "N/A",
                avgEngagementScore: "N/A",
                avgDistractionScore: "N/A",
                avgAccelerationScore: "N/A",
            };
            setFleetPerformance(performanceGraph);
        }
    }, [userSession, isApiFailed, performanceScoreData]);

    const handleCheckBoxChange = (typeOfCheckbox) => {
        /** @todo: need to refactor this code */
        if (typeOfCheckbox === "braking") {
            trackEvents("Fleet Performance(Analytics)", {
                name: typeOfCheckbox,
                value:
                    fleetPerformance.driverPerformanceCheckbox.braking === true
                        ? "Unchecked"
                        : "Checked",
            }, params);
            if (fleetPerformance.driverPerformanceCheckbox.braking === true) {
                let driverPerformanceLabels = [
                    ...fleetPerformance.driverPerformance.labels,
                ];
                const _driverPerformanceCheckbox = {
                    ...fleetPerformance.driverPerformanceCheckbox,
                    braking: false,
                    brakingData: fleetPerformance.driverPerformance.datasets.filter(
                        (dataset) => dataset.label === t("BRAKING")
                    ),
                };
                const driverPerformanceDataset =
                    fleetPerformance.driverPerformance.datasets.filter(
                        (dataset) => dataset.label !== t("BRAKING")
                    );
                const _driverPerformance = {
                    labels: [...driverPerformanceLabels],
                    datasets: [...driverPerformanceDataset],
                };
                setFleetPerformance({
                    ...fleetPerformance,
                    driverPerformance: _driverPerformance,
                    driverPerformanceCheckbox: _driverPerformanceCheckbox,
                });
            } else {
                const _driverPerformanceCheckbox = {
                    ...fleetPerformance.driverPerformanceCheckbox,
                    braking: true,
                };
                let driverPerformanceLabels = [
                    ...fleetPerformance.driverPerformance.labels,
                ];
                const _driverPerformance = {
                    labels: [...driverPerformanceLabels],
                    datasets: [
                        ...fleetPerformance.driverPerformance.datasets,
                        ...fleetPerformance.driverPerformanceCheckbox.brakingData,
                    ],
                };
                setFleetPerformance({
                    ...fleetPerformance,
                    driverPerformance: _driverPerformance,
                    driverPerformanceCheckbox: _driverPerformanceCheckbox,
                });
            }
        }
        if (typeOfCheckbox === "cornering") {
            trackEvents("Fleet Performance(Analytics)", {
                name: typeOfCheckbox,
                value:
                    fleetPerformance.driverPerformanceCheckbox.cornering === true
                        ? "Unchecked"
                        : "Checked",
            }, params);
            if (fleetPerformance.driverPerformanceCheckbox.cornering === true) {
                let driverPerformanceLabels = [
                    ...fleetPerformance.driverPerformance.labels,
                ];
                const _driverPerformanceCheckbox = {
                    ...fleetPerformance.driverPerformanceCheckbox,
                    cornering: false,
                    corneringData: fleetPerformance.driverPerformance.datasets.filter(
                        (dataset) => dataset.label === t("CORNERING")
                    ),
                };
                const driverPerformanceDataset =
                    fleetPerformance.driverPerformance.datasets.filter(
                        (dataset) => dataset.label !== t("CORNERING")
                    );
                const _driverPerformance = {
                    labels: [...driverPerformanceLabels],
                    datasets: [...driverPerformanceDataset],
                };
                setFleetPerformance({
                    ...fleetPerformance,
                    driverPerformance: _driverPerformance,
                    driverPerformanceCheckbox: _driverPerformanceCheckbox,
                });
            } else {
                const _driverPerformanceCheckbox = {
                    ...fleetPerformance.driverPerformanceCheckbox,
                    cornering: true,
                };
                let driverPerformanceLabels = [
                    ...fleetPerformance.driverPerformance.labels,
                ];
                const _driverPerformance = {
                    labels: [...driverPerformanceLabels],
                    datasets: [
                        ...fleetPerformance.driverPerformance.datasets,
                        ...fleetPerformance.driverPerformanceCheckbox.corneringData,
                    ],
                };
                setFleetPerformance({
                    ...fleetPerformance,
                    driverPerformance: _driverPerformance,
                    driverPerformanceCheckbox: _driverPerformanceCheckbox,
                });
            }
        }
        if (typeOfCheckbox === "speeding") {
            trackEvents("Fleet Performance(Analytics)", {
                name: typeOfCheckbox,
                value:
                    fleetPerformance.driverPerformanceCheckbox.speeding === true
                        ? "Unchecked"
                        : "Checked",
            }, params);

            if (fleetPerformance.driverPerformanceCheckbox.speeding === true) {
                let driverPerformanceLabels = [
                    ...fleetPerformance.driverPerformance.labels,
                ];
                const _driverPerformanceCheckbox = {
                    ...fleetPerformance.driverPerformanceCheckbox,
                    speeding: false,
                    speedingData: fleetPerformance.driverPerformance.datasets.filter(
                        (dataset) => dataset.label === t("SPEEDING")
                    ),
                };
                const driverPerformanceDataset =
                    fleetPerformance.driverPerformance.datasets.filter(
                        (dataset) => dataset.label !== t("SPEEDING")
                    );
                const _driverPerformance = {
                    labels: [...driverPerformanceLabels],
                    datasets: [...driverPerformanceDataset],
                };
                setFleetPerformance({
                    ...fleetPerformance,
                    driverPerformance: _driverPerformance,
                    driverPerformanceCheckbox: _driverPerformanceCheckbox,
                });
            } else {
                const _driverPerformanceCheckbox = {
                    ...fleetPerformance.driverPerformanceCheckbox,
                    speeding: true,
                };
                let driverPerformanceLabels = [
                    ...fleetPerformance.driverPerformance.labels,
                ];
                const _driverPerformance = {
                    labels: [...driverPerformanceLabels],
                    datasets: [
                        ...fleetPerformance.driverPerformance.datasets,
                        ...fleetPerformance.driverPerformanceCheckbox.speedingData,
                    ],
                };
                setFleetPerformance({
                    ...fleetPerformance,
                    driverPerformance: _driverPerformance,
                    driverPerformanceCheckbox: _driverPerformanceCheckbox,
                });
            }
        }
        if (typeOfCheckbox === "engagement") {
            trackEvents("Fleet Performance(Analytics)", {
                name: typeOfCheckbox,
                value:
                    fleetPerformance.driverPerformanceCheckbox.engagement === true
                        ? "Unchecked"
                        : "Checked",
            }, params);

            if (fleetPerformance.driverPerformanceCheckbox.engagement === true) {
                let driverPerformanceLabels = [
                    ...fleetPerformance.driverPerformance.labels,
                ];
                const _driverPerformanceCheckbox = {
                    ...fleetPerformance.driverPerformanceCheckbox,
                    engagement: false,
                    engagementData: fleetPerformance.driverPerformance.datasets.filter(
                        (dataset) => dataset.label === t("ENGAGEMENT")
                    ),
                };
                const driverPerformanceDataset =
                    fleetPerformance.driverPerformance.datasets.filter(
                        (dataset) => dataset.label !== t("ENGAGEMENT")
                    );
                const _driverPerformance = {
                    labels: [...driverPerformanceLabels],
                    datasets: [...driverPerformanceDataset],
                };
                setFleetPerformance({
                    ...fleetPerformance,
                    driverPerformance: _driverPerformance,
                    driverPerformanceCheckbox: _driverPerformanceCheckbox,
                });
            } else {
                const _driverPerformanceCheckbox = {
                    ...fleetPerformance.driverPerformanceCheckbox,
                    engagement: true,
                };
                let driverPerformanceLabels = [
                    ...fleetPerformance.driverPerformance.labels,
                ];
                const _driverPerformance = {
                    labels: [...driverPerformanceLabels],
                    datasets: [
                        ...fleetPerformance.driverPerformance.datasets,
                        ...fleetPerformance.driverPerformanceCheckbox.engagementData,
                    ],
                };
                setFleetPerformance({
                    ...fleetPerformance,
                    driverPerformance: _driverPerformance,
                    driverPerformanceCheckbox: _driverPerformanceCheckbox,
                });
            }
        }
        if (typeOfCheckbox === "distraction") {
            trackEvents("Fleet Performance(Analytics)", {
                name: typeOfCheckbox,
                value:
                    fleetPerformance.driverPerformanceCheckbox.distraction === true
                        ? "Unchecked"
                        : "Checked",
            }, params);

            if (fleetPerformance.driverPerformanceCheckbox.distraction === true) {
                let driverPerformanceLabels = [
                    ...fleetPerformance.driverPerformance.labels,
                ];
                const _driverPerformanceCheckbox = {
                    ...fleetPerformance.driverPerformanceCheckbox,
                    distraction: false,
                    distractionData: fleetPerformance.driverPerformance.datasets.filter(
                        (dataset) => dataset.label === t("DISTRACTION")
                    ),
                };
                const driverPerformanceDataset =
                    fleetPerformance.driverPerformance.datasets.filter(
                        (dataset) => dataset.label !== t("DISTRACTION")
                    );
                const _driverPerformance = {
                    labels: [...driverPerformanceLabels],
                    datasets: [...driverPerformanceDataset],
                };
                setFleetPerformance({
                    ...fleetPerformance,
                    driverPerformance: _driverPerformance,
                    driverPerformanceCheckbox: _driverPerformanceCheckbox,
                });
            } else {
                const _driverPerformanceCheckbox = {
                    ...fleetPerformance.driverPerformanceCheckbox,
                    distraction: true,
                };
                let driverPerformanceLabels = [
                    ...fleetPerformance.driverPerformance.labels,
                ];
                const _driverPerformance = {
                    labels: [...driverPerformanceLabels],
                    datasets: [
                        ...fleetPerformance.driverPerformance.datasets,
                        ...fleetPerformance.driverPerformanceCheckbox.distractionData,
                    ],
                };
                setFleetPerformance({
                    ...fleetPerformance,
                    driverPerformance: _driverPerformance,
                    driverPerformanceCheckbox: _driverPerformanceCheckbox,
                });
            }
        }
        if (typeOfCheckbox === "acceleration") {
            trackEvents("Fleet Performance(Analytics)", {
                name: typeOfCheckbox,
                value:
                    fleetPerformance.driverPerformanceCheckbox.acceleration === true
                        ? "Unchecked"
                        : "Checked",
            }, params);

            if (fleetPerformance.driverPerformanceCheckbox.acceleration === true) {
                let driverPerformanceLabels = [
                    ...fleetPerformance.driverPerformance.labels,
                ];
                const _driverPerformanceCheckbox = {
                    ...fleetPerformance.driverPerformanceCheckbox,
                    acceleration: false,
                    accelerationData: fleetPerformance.driverPerformance.datasets.filter(
                        (dataset) => dataset.label === t("ACCELERATION")
                    ),
                };
                const driverPerformanceDataset =
                    fleetPerformance.driverPerformance.datasets.filter(
                        (dataset) => dataset.label !== t("ACCELERATION")
                    );
                const _driverPerformance = {
                    labels: [...driverPerformanceLabels],
                    datasets: [...driverPerformanceDataset],
                };
                setFleetPerformance({
                    ...fleetPerformance,
                    driverPerformance: _driverPerformance,
                    driverPerformanceCheckbox: _driverPerformanceCheckbox,
                });
            } else {
                const _driverPerformanceCheckbox = {
                    ...fleetPerformance.driverPerformanceCheckbox,
                    acceleration: true,
                };
                let driverPerformanceLabels = [
                    ...fleetPerformance.driverPerformance.labels,
                ];
                const _driverPerformance = {
                    labels: [...driverPerformanceLabels],
                    datasets: [
                        ...fleetPerformance.driverPerformance.datasets,
                        ...fleetPerformance.driverPerformanceCheckbox.accelerationData,
                    ],
                };
                setFleetPerformance({
                    ...fleetPerformance,
                    driverPerformance: _driverPerformance,
                    driverPerformanceCheckbox: _driverPerformanceCheckbox,
                });
            }
        }
    };

    return (
        <>
            <CustomGrid container spacing={4} justifyContent="space-between">
                <CustomGrid item xs={12}>
                    <CustomPaper square elevation={0}>
                        <CustomCard
                            elevation={0}
                            className={clsx(
                                classes.cardContentWrapper,
                                isApiFailed ? classes.fleetPerformanceDisabled : null
                            )}
                        >
                            <CustomCardContent
                                className={classes.cardContent}
                                style={{ padding: "1.5em 3em" }}
                            >
                                <CustomGrid
                                    container
                                    justifyContent="space-between"
                                    alignItems="center"
                                    spacing={0}
                                >
                                    <CustomGrid item>
                                        <CustomTypography
                                            component="h1"
                                            variant="h5"
                                            className={classes.title}
                                        >
                                            {getRBAC_PerformanceCard &&
                                                getRBAC_PerformanceCard.isGroupScope ===
                                                EntityAccess.ALLOWED &&
                                                getRBAC_PerformanceCard.isAccessible ===
                                                EntityAccess.ALLOWED
                                                ? t("FLEET_PERFORMANCE")
                                                : t("DRIVER_PERFORMANCE")}{" "}
                                        </CustomTypography>
                                        {fleetPerformance && !fleetPerformance.loading && (
                                            <div className={classes.performanceCheckboxContainer}>
                                                {isShowBraking && (

                                                    <span
                                                        className={clsx(
                                                            classes.performanceCheckbox,
                                                            isApiFailed ||
                                                                isApi ||
                                                                (!isApi &&
                                                                    (!performanceScoreData ||
                                                                        !performanceScoreData.brakingScoreData ||
                                                                        !performanceScoreData.brakingScoreData
                                                                            .length || fleetPerformance.avgBrakingScore === "N/A"))
                                                                ? classes.performanceCheckboxApiFailed
                                                                : null
                                                        )}
                                                    >
                                                        <CustomCheckbox
                                                            checked={
                                                                fleetPerformance.driverPerformanceCheckbox.braking
                                                            }
                                                            onChange={() => handleCheckBoxChange("braking")}
                                                            color="primary"
                                                            inputProps={{ "aria-label": "primary checkbox" }}
                                                        />

                                                        <CustomTypography
                                                            component="p"
                                                            className={classes.brakingLegend}
                                                        >
                                                            {t("BRAKING")}{" "}
                                                            <span className={classes.avgScoreLabel}>
                                                                ({t("AVG_SCORE")}: {fleetPerformance.avgBrakingScore})
                                                            </span>
                                                        </CustomTypography>
                                                    </span>

                                                )}
                                                {
                                                    isShowCornering && (
                                                        <span
                                                            className={clsx(
                                                                classes.performanceCheckbox,
                                                                isApiFailed ||
                                                                    isApi ||
                                                                    (!isApi &&
                                                                        (!performanceScoreData ||
                                                                            !performanceScoreData.corneringScoreData ||
                                                                            !performanceScoreData.corneringScoreData
                                                                                .length || fleetPerformance.avgCorneringScore === "N/A"))
                                                                    ? classes.performanceCheckboxApiFailed
                                                                    : null
                                                            )}
                                                        >
                                                            <CustomCheckbox
                                                                checked={
                                                                    fleetPerformance.driverPerformanceCheckbox
                                                                        .cornering
                                                                }
                                                                onChange={() => handleCheckBoxChange("cornering")}
                                                                color="primary"
                                                                inputProps={{ "aria-label": "primary checkbox" }}
                                                            />
                                                            <CustomTypography
                                                                component="p"
                                                                className={classes.corneringLegend}
                                                            >
                                                                {t("CORNERING")}{" "}
                                                                <span className={classes.avgScoreLabel}>
                                                                    ({t("AVG_SCORE")}: {fleetPerformance.avgCorneringScore})
                                                                </span>
                                                            </CustomTypography>
                                                        </span>
                                                    )
                                                }
                                                {
                                                    isShowSpeeding && (
                                                        <span
                                                            className={clsx(
                                                                classes.performanceCheckbox,
                                                                isApiFailed ||
                                                                    isApi ||
                                                                    (!isApi &&
                                                                        (!performanceScoreData ||
                                                                            !performanceScoreData.speedingScoreData ||
                                                                            !performanceScoreData.speedingScoreData
                                                                                .length || fleetPerformance.avgSpeedingScore === "N/A"))
                                                                    ? classes.performanceCheckboxApiFailed
                                                                    : null
                                                            )}
                                                        >
                                                            <CustomCheckbox
                                                                checked={
                                                                    fleetPerformance.driverPerformanceCheckbox
                                                                        .speeding
                                                                }
                                                                onChange={() => handleCheckBoxChange("speeding")}
                                                                color="primary"
                                                                inputProps={{ "aria-label": "primary checkbox" }}
                                                            />
                                                            <CustomTypography
                                                                component="p"
                                                                className={classes.speedingLegend}
                                                            >
                                                                {t("SPEEDING")}{" "}
                                                                <span className={classes.avgScoreLabel}>
                                                                    ({t("AVG_SCORE")}: {fleetPerformance.avgSpeedingScore})
                                                                </span>
                                                            </CustomTypography>
                                                        </span>
                                                    )
                                                }
                                                {
                                                    isShowAcceleration && (
                                                        <span
                                                            className={clsx(
                                                                classes.performanceCheckbox,
                                                                isApiFailed ||
                                                                    isApi ||
                                                                    (!isApi &&
                                                                        (!performanceScoreData ||
                                                                            !performanceScoreData.accelerationScoreData ||
                                                                            !performanceScoreData.accelerationScoreData
                                                                                .length || fleetPerformance.avgAccelerationScore === "N/A"))
                                                                    ? classes.performanceCheckboxApiFailed
                                                                    : null
                                                            )}
                                                        >
                                                            <CustomCheckbox
                                                                checked={
                                                                    fleetPerformance.driverPerformanceCheckbox
                                                                        .acceleration
                                                                }
                                                                onChange={() => handleCheckBoxChange("acceleration")}
                                                                color="primary"
                                                                inputProps={{ "aria-label": "primary checkbox" }}
                                                            />
                                                            <CustomTypography
                                                                component="p"
                                                                className={classes.accelerationLegend}
                                                            >
                                                                {t("ACCELERATION")}{" "}
                                                                <span className={classes.avgScoreLabel}>
                                                                    ({t("AVG_SCORE")}: {fleetPerformance.avgAccelerationScore})
                                                                </span>
                                                            </CustomTypography>
                                                        </span>
                                                    )
                                                }

                                                <span
                                                    className={clsx(
                                                        classes.performanceCheckbox,
                                                        isApiFailed ||
                                                            isApi ||
                                                            (!isApi &&
                                                                (!performanceScoreData ||
                                                                    !performanceScoreData.engagementScoreData ||
                                                                    !performanceScoreData.engagementScoreData
                                                                        .length || fleetPerformance.avgEngagementScore === "N/A"))
                                                            ? classes.performanceCheckboxApiFailed
                                                            : null
                                                    )}
                                                >
                                                    <CustomCheckbox
                                                        checked={
                                                            fleetPerformance.driverPerformanceCheckbox
                                                                .engagement
                                                        }
                                                        onChange={() => handleCheckBoxChange("engagement")}
                                                        color="primary"
                                                        inputProps={{ "aria-label": "primary checkbox" }}
                                                    />
                                                    <CustomTypography
                                                        component="p"
                                                        className={classes.engagementLegend}
                                                    >
                                                        {t("CONTENT_ENGAGEMENT")}{" "}
                                                        <span className={classes.avgScoreLabel}>
                                                            ({t("AVG_SCORE")}: {fleetPerformance.avgEngagementScore}
                                                            )
                                                        </span>
                                                    </CustomTypography>
                                                </span>

                                                {
                                                    isShowDistraction && (
                                                        <span
                                                            className={clsx(
                                                                classes.performanceCheckbox,
                                                                isApiFailed ||
                                                                    isApi ||
                                                                    (!isApi &&
                                                                        (!performanceScoreData ||
                                                                            !performanceScoreData.distractionScoreData ||
                                                                            !performanceScoreData.distractionScoreData
                                                                                .length || fleetPerformance.avgDistractionScore === "N/A"))
                                                                    ? classes.performanceCheckboxApiFailed
                                                                    : null
                                                            )}
                                                        >
                                                            <CustomCheckbox
                                                                checked={
                                                                    fleetPerformance.driverPerformanceCheckbox
                                                                        .distraction
                                                                }
                                                                onChange={() => handleCheckBoxChange("distraction")}
                                                                color="primary"
                                                                inputProps={{ "aria-label": "primary checkbox" }}
                                                            />
                                                            <CustomTypography
                                                                component="p"
                                                                className={classes.distractionLegend}
                                                            >
                                                                {t("MOBILE_DISTRACTION")}{" "}
                                                                <span className={classes.avgScoreLabel}>
                                                                    ({t("AVG_SCORE")}: {fleetPerformance.avgDistractionScore})
                                                                </span>
                                                            </CustomTypography>
                                                        </span>
                                                    )
                                                }

                                            </div>
                                        )}
                                    </CustomGrid>
                                </CustomGrid>
                                <CustomGrid container>
                                    <CustomGrid item xs={12}>
                                        {fleetPerformance && fleetPerformance.loading && (
                                            <CustomCircularProgress />
                                        )}
                                        {fleetPerformance && !fleetPerformance.loading && (
                                            <CustomLineChart
                                                className={classes.reportGraph}
                                                data={fleetPerformance.driverPerformance}
                                                options={{
                                                    legend: { display: false },
                                                    scales: {
                                                        xAxes: [{ gridLines: { display: false } }],
                                                        yAxes: [
                                                            {
                                                                ticks: { beginAtZero: false },
                                                                gridLines: { display: false },
                                                            },
                                                        ],
                                                    },
                                                }}
                                                height={70}
                                            />
                                        )}
                                    </CustomGrid>
                                </CustomGrid>
                            </CustomCardContent>
                        </CustomCard>
                    </CustomPaper>
                </CustomGrid>
            </CustomGrid>
        </>
    );
}
